import { useEffect, useState, useCallback } from 'react'
import { Link } from 'react-router-dom'
import { api } from '../lib/api'
import { useToast } from '../components/ui/Toast'
import { GlowCard } from '../components/ui/GlowCard'

interface SiteOverviewData {
  id: string
  slug: string
  name: string
  address: string | null
  city: string | null
  state: string | null
  status: string
  wan_ip: string | null
  client_name: string | null
  total_devices: number
  online_devices: number
  offline_devices: number
  online_clients: number
  total_clients: number
  total_poe_draw: number
  last_synced_at: string | null
}

interface OverviewTotals {
  total_devices: number
  online_devices: number
  offline_devices: number
  total_clients: number
  online_clients: number
  total_poe_draw: number
}

interface SearchResult {
  devices: Array<{ id: string; slug: string; label: string; hostname: string | null; ip_address: string | null; mac_address: string | null; device_type: string | null; status: string; site_name: string; site_slug: string }>
  clients: Array<{ id: string; mac: string; ip: string | null; hostname: string | null; display_name: string | null; oui: string | null; connection_type: string | null; is_online: number; site_name: string; site_slug: string }>
  sites: Array<{ id: string; slug: string; name: string; address: string | null; city: string | null; state: string | null; client_name: string | null; device_count: number }>
}

function timeAgo(dateStr: string | null): string {
  if (!dateStr) return 'Never'
  const diff = Date.now() - new Date(dateStr).getTime()
  const mins = Math.floor(diff / 60000)
  if (mins < 1) return 'Just now'
  if (mins < 60) return `${mins}m ago`
  const hours = Math.floor(mins / 60)
  if (hours < 24) return `${hours}h ago`
  return `${Math.floor(hours / 24)}d ago`
}

export default function NetworkOverview() {
  const [sites, setSites] = useState<SiteOverviewData[]>([])
  const [totals, setTotals] = useState<OverviewTotals | null>(null)
  const [loading, setLoading] = useState(true)
  const [search, setSearch] = useState('')
  const [searchResults, setSearchResults] = useState<SearchResult | null>(null)
  const [searching, setSearching] = useState(false)
  const [filter, setFilter] = useState<'all' | 'issues'>('all')
  const { toast } = useToast()

  const loadOverview = useCallback(() => {
    api.get<{ sites: SiteOverviewData[]; totals: OverviewTotals }>('/network/overview')
      .then(resp => {
        setSites(resp.sites || [])
        setTotals(resp.totals || null)
      })
      .catch(err => toast(`Failed to load overview: ${err?.message}`, 'error'))
      .finally(() => setLoading(false))
  }, [toast])

  useEffect(() => { loadOverview() }, [loadOverview])

  // Debounced search
  useEffect(() => {
    if (!search || search.length < 2) {
      setSearchResults(null)
      return
    }
    const timer = setTimeout(() => {
      setSearching(true)
      api.get<SearchResult>(`/network/search?q=${encodeURIComponent(search)}`)
        .then(setSearchResults)
        .catch(() => {})
        .finally(() => setSearching(false))
    }, 300)
    return () => clearTimeout(timer)
  }, [search])

  const filteredSites = filter === 'issues'
    ? sites.filter(s => s.offline_devices > 0)
    : sites

  const issueCount = sites.filter(s => s.offline_devices > 0).length

  if (loading) return <div className="skeleton" style={{ height: 400, borderRadius: 12 }} />

  return (
    <div className="page-enter">
      <div style={{ marginBottom: 20 }}>
        <h1 style={{ fontSize: 22, fontWeight: 800, letterSpacing: '-0.02em' }}>Network Overview</h1>
        {totals && (
          <div style={{ fontSize: 12, color: 'var(--text-tertiary)', marginTop: 4 }}>
            {totals.total_devices} devices across {sites.length} sites
            {totals.offline_devices > 0 && <span style={{ color: 'var(--err)' }}> · {totals.offline_devices} offline</span>}
            {' · '}{totals.online_clients} clients connected
          </div>
        )}
      </div>

      {/* Top bar: search + filters */}
      <div style={{ display: 'flex', gap: 8, marginBottom: 20, flexWrap: 'wrap' }}>
        <div style={{ position: 'relative', flex: 1, maxWidth: 400 }}>
          <input
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder="Search across all sites (MAC, IP, hostname, device name...)"
            className="input"
            style={{ fontSize: 12, padding: '8px 12px', width: '100%' }}
          />
          {/* Search results dropdown */}
          {searchResults && search.length >= 2 && (
            <div style={{
              position: 'absolute', top: '100%', left: 0, right: 0, marginTop: 4, zIndex: 999,
              background: 'var(--bg-elevated)', border: '1px solid var(--border)', borderRadius: 8,
              boxShadow: '0 8px 24px rgba(0,0,0,0.15)', maxHeight: 400, overflowY: 'auto',
            }}>
              {searching && <div style={{ padding: 12, fontSize: 12, color: 'var(--text-tertiary)' }}>Searching...</div>}

              {searchResults.devices.length > 0 && (
                <div>
                  <div style={{ fontSize: 10, fontWeight: 800, color: 'var(--text-disabled)', padding: '8px 12px 4px', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                    Devices ({searchResults.devices.length})
                  </div>
                  {searchResults.devices.map(d => (
                    <Link key={d.id} to={`/sites/${d.site_slug}`} onClick={() => { setSearch(''); setSearchResults(null) }}
                      style={{ display: 'flex', justifyContent: 'space-between', padding: '6px 12px', textDecoration: 'none', color: 'var(--text)' }}
                      onMouseEnter={e => e.currentTarget.style.background = 'var(--bg-overlay)'}
                      onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
                    >
                      <div>
                        <span style={{ fontSize: 12, fontWeight: 600 }}>{d.label || d.hostname || d.mac_address}</span>
                        <span style={{ fontSize: 11, color: 'var(--text-tertiary)', marginLeft: 6 }}>{d.ip_address || ''}</span>
                      </div>
                      <div style={{ fontSize: 11, color: 'var(--text-tertiary)' }}>
                        <span style={{ color: d.status === 'online' ? 'var(--ok)' : 'var(--err)', marginRight: 4 }}>{'\u25CF'}</span>
                        {d.site_name}
                      </div>
                    </Link>
                  ))}
                </div>
              )}

              {searchResults.clients.length > 0 && (
                <div>
                  <div style={{ fontSize: 10, fontWeight: 800, color: 'var(--text-disabled)', padding: '8px 12px 4px', textTransform: 'uppercase', letterSpacing: '0.05em', borderTop: '1px solid var(--border)' }}>
                    Clients ({searchResults.clients.length})
                  </div>
                  {searchResults.clients.map(cl => (
                    <Link key={cl.id} to={`/sites/${cl.site_slug}`} onClick={() => { setSearch(''); setSearchResults(null) }}
                      style={{ display: 'flex', justifyContent: 'space-between', padding: '6px 12px', textDecoration: 'none', color: 'var(--text)' }}
                      onMouseEnter={e => e.currentTarget.style.background = 'var(--bg-overlay)'}
                      onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
                    >
                      <div>
                        <span style={{ fontSize: 12, fontWeight: 600 }}>{cl.display_name || cl.hostname || cl.oui || cl.mac}</span>
                        <span style={{ fontSize: 11, fontFamily: 'var(--font-mono)', color: 'var(--text-tertiary)', marginLeft: 6 }}>{cl.ip || cl.mac}</span>
                      </div>
                      <div style={{ fontSize: 11, color: 'var(--text-tertiary)' }}>
                        <span style={{ color: cl.is_online ? 'var(--ok)' : 'var(--text-2)', marginRight: 4 }}>{'\u25CF'}</span>
                        {cl.site_name}
                      </div>
                    </Link>
                  ))}
                </div>
              )}

              {searchResults.sites.length > 0 && (
                <div>
                  <div style={{ fontSize: 10, fontWeight: 800, color: 'var(--text-disabled)', padding: '8px 12px 4px', textTransform: 'uppercase', letterSpacing: '0.05em', borderTop: '1px solid var(--border)' }}>
                    Sites ({searchResults.sites.length})
                  </div>
                  {searchResults.sites.map(s => (
                    <Link key={s.id} to={`/sites/${s.slug}`} onClick={() => { setSearch(''); setSearchResults(null) }}
                      style={{ display: 'flex', justifyContent: 'space-between', padding: '6px 12px', textDecoration: 'none', color: 'var(--text)' }}
                      onMouseEnter={e => e.currentTarget.style.background = 'var(--bg-overlay)'}
                      onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
                    >
                      <div>
                        <span style={{ fontSize: 12, fontWeight: 600 }}>{s.name}</span>
                        {s.client_name && <span style={{ fontSize: 11, color: 'var(--text-tertiary)', marginLeft: 6 }}>{s.client_name}</span>}
                      </div>
                      <span style={{ fontSize: 11, color: 'var(--text-tertiary)' }}>{s.device_count} devices</span>
                    </Link>
                  ))}
                </div>
              )}

              {!searching && searchResults.devices.length === 0 && searchResults.clients.length === 0 && searchResults.sites.length === 0 && (
                <div style={{ padding: 16, fontSize: 12, color: 'var(--text-tertiary)', textAlign: 'center' }}>
                  No results found
                </div>
              )}
            </div>
          )}
        </div>
        <div style={{ display: 'flex', gap: 4 }}>
          <button
            onClick={() => setFilter('all')}
            className={filter === 'all' ? 'btn-primary' : 'btn-secondary'}
            style={{ fontSize: 11, padding: '6px 12px' }}
          >
            All Sites
          </button>
          <button
            onClick={() => setFilter('issues')}
            className={filter === 'issues' ? 'btn-primary' : 'btn-secondary'}
            style={{ fontSize: 11, padding: '6px 12px' }}
          >
            Issues{issueCount > 0 ? ` (${issueCount})` : ''}
          </button>
        </div>
      </div>

      {/* Summary cards */}
      {totals && (
        <div style={{ display: 'flex', gap: 12, marginBottom: 20, flexWrap: 'wrap' }}>
          <StatCard label="Total Devices" value={totals.total_devices} delay={0} />
          <StatCard label="Online" value={totals.online_devices} color="var(--ok)" delay={50} />
          {totals.offline_devices > 0 && <StatCard label="Offline" value={totals.offline_devices} color="var(--err)" delay={100} />}
          <StatCard label="Clients" value={totals.online_clients} delay={150} />
          {totals.total_poe_draw > 0 && <StatCard label="PoE Draw" value={`${totals.total_poe_draw.toFixed(0)}W`} delay={200} />}
        </div>
      )}

      {/* Site grid */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))', gap: 12 }}>
        {filteredSites.map(site => {
          const hasIssues = site.offline_devices > 0
          return (
            <Link key={site.id} to={`/sites/${site.slug}`} style={{ textDecoration: 'none', color: 'inherit' }}>
              <div className="card" style={{
                padding: 16, cursor: 'pointer', transition: 'all 0.15s ease',
                borderLeft: `3px solid ${hasIssues ? 'var(--err)' : 'var(--ok)'}`,
              }}
              onMouseEnter={e => { e.currentTarget.style.transform = 'translateY(-2px)'; e.currentTarget.style.boxShadow = '0 4px 12px rgba(0,0,0,0.1)' }}
              onMouseLeave={e => { e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.boxShadow = '' }}
              >
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
                  <div>
                    <div style={{ fontSize: 14, fontWeight: 700 }}>{site.name}</div>
                    {site.client_name && <div style={{ fontSize: 11, color: 'var(--text-tertiary)' }}>{site.client_name}</div>}
                  </div>
                  <span style={{ fontSize: 16, color: hasIssues ? 'var(--err)' : 'var(--ok)' }}>
                    {hasIssues ? '\u26A0' : '\u2713'}
                  </span>
                </div>

                <div style={{ display: 'flex', gap: 16, fontSize: 12, color: 'var(--text-secondary)' }}>
                  <div>
                    <span style={{ fontWeight: 700, fontFamily: 'var(--font-mono)' }}>{site.total_devices}</span> devices
                    {site.offline_devices > 0 && (
                      <span style={{ color: 'var(--err)', fontWeight: 600 }}> ({site.offline_devices} offline)</span>
                    )}
                  </div>
                  <div>
                    <span style={{ fontWeight: 700, fontFamily: 'var(--font-mono)' }}>{site.online_clients}</span> clients
                  </div>
                </div>

                <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 8, fontSize: 11, color: 'var(--text-tertiary)' }}>
                  <span>{[site.city, site.state].filter(Boolean).join(', ') || site.address || ''}</span>
                  <span>Synced {timeAgo(site.last_synced_at)}</span>
                </div>
              </div>
            </Link>
          )
        })}
      </div>

      {filteredSites.length === 0 && (
        <div style={{ textAlign: 'center', padding: 40, color: 'var(--text-tertiary)' }}>
          <p style={{ fontSize: 14 }}>
            {filter === 'issues' ? 'No sites with issues.' : 'No sites with network devices found.'}
          </p>
          <p style={{ fontSize: 12, marginTop: 4 }}>Sync a UniFi controller to see network data here.</p>
        </div>
      )}
    </div>
  )
}

function StatCard({ label, value, color, delay = 0 }: { label: string; value: number | string; color?: string; delay?: number }) {
  return (
    <GlowCard style={{ padding: '10px 18px', textAlign: 'center' }} delay={delay}>
      <div style={{ fontSize: 20, fontWeight: 900, fontFamily: 'var(--font-mono)', color: color || 'var(--text)' }}>{value}</div>
      <div style={{ fontSize: 10, color: 'var(--text-disabled)', textTransform: 'uppercase', letterSpacing: '0.06em', fontWeight: 700 }}>{label}</div>
    </GlowCard>
  )
}
